import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useGame } from '../context/GameContext.jsx';
import { Scanner } from '@yudiel/react-qr-scanner';

const QrScannerModal = () => {
  const { translations, lang, handleQrCodeScanned, setError, qrScannerModalRef } = useGame();

  const [qrResult, setQrResult] = useState(''); 
  const [isScanning, setIsScanning] = useState(false);
  const [facingMode, setFacingMode] = useState('environment');

  const bsModalInstance = useRef(null);

  const handleScanResult = useCallback((result) => {
    if (result && result.length > 0 && result[0] && result[0].rawValue) {
      const scannedData = result[0].rawValue;
      console.log('QR Code result structure (Fixed):', result);
      console.log('QR Code detected (Raw value):', scannedData);
      
      setQrResult(`${translations[lang]?.scanned_prefix}: ${scannedData}`);
      handleQrCodeScanned(scannedData); 
      if (bsModalInstance.current) {
        bsModalInstance.current.hide();
      }
    } else {
      console.warn('handleScanResult: Invalid QR Code result.', result);
      setQrResult(translations[lang]?.qr_code_unrecognized);
    }
  }, [lang, translations, handleQrCodeScanned]);

  const handleScanError = useCallback((err) => {
    console.error('QR scanner error:', err);
    if (err.name === 'NotAllowedError' || err.name === 'NotFoundError') {
      setError(translations[lang]?.qr_scan_error_camera_access);
    } else {
      setError(translations[lang]?.unknown_error_occurred);
    }
    setQrResult(translations[lang]?.qr_scan_stopped);
    if (bsModalInstance.current) {
      bsModalInstance.current.hide();
    }
  }, [lang, translations, setError]);

  const handleCloseScanner = useCallback(() => {
    if (bsModalInstance.current) {
      bsModalInstance.current.hide();
      console.log('Scanner closed by screen click.');
    }
  }, []);

  useEffect(() => {
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    console.log(`Device detected: ${isMobile ? 'Mobile' : 'Desktop'}`);
    
    setFacingMode(isMobile ? 'environment' : 'user');
  }, []);

  const toggleCamera = useCallback(() => {
    setFacingMode(prev => (prev === 'environment' ? 'user' : 'environment'));
  }, []);

  useEffect(() => {
    const modalElement = qrScannerModalRef.current; 
    
    const onModalShown = () => {
      setIsScanning(true); 
      setQrResult(translations[lang]?.qr_scan_looking);
      setFacingMode('environment');
    };
    const onModalHidden = () => {
      setIsScanning(false); 
      setQrResult('');
    };

    if (modalElement && window.bootstrap) {
      const bsModal = window.bootstrap.Modal.getInstance(modalElement) || new window.bootstrap.Modal(modalElement, {
        backdrop: 'static',
        keyboard: false
      });
      bsModalInstance.current = bsModal;

      modalElement.bsModal = {
        show: () => bsModalInstance.current.show(),
        hide: () => bsModalInstance.current.hide()
      };

      modalElement.addEventListener('shown.bs.modal', onModalShown);
      modalElement.addEventListener('hidden.bs.modal', onModalHidden);
    }

    return () => {
      if (modalElement) {
        modalElement.removeEventListener('shown.bs.modal', onModalShown);
        modalElement.removeEventListener('hidden.bs.modal', onModalHidden);
      }
      if (bsModalInstance.current) {
        bsModalInstance.current.dispose();
        bsModalInstance.current = null;
      }
    };
  }, [qrScannerModalRef, translations, lang]); 

  const mirrorStyle = facingMode === 'user' ? 'scaleX(-1)' : 'scaleX(1)';

  return (
    <div className="modal fade" id="qrScannerModal" tabIndex="-1" aria-labelledby="qrScannerModalLabel" aria-hidden="true" ref={qrScannerModalRef} onClick={handleCloseScanner}>
      <div className="modal-dialog modal-fullscreen m-0 p-0">
        <div className="modal-content bg-dark text-light m-0 p-0">
          <div className="modal-body text-center m-0 p-0 d-flex flex-column justify-content-center align-items-center">
            {isScanning ? (
              <div className="qr-scanner-container position-relative w-100 h-100 d-flex flex-column justify-content-center align-items-center">
                <Scanner 
                  onScan={handleScanResult}
                  onError={handleScanError}
                  scanDelay={500}
                  constraints={{ facingMode: facingMode }}
                />
                <style>{`
                  video {
                    transform: ${mirrorStyle};
                  }
                `}</style>
                <div className="qr-controls position-absolute bottom-0 w-100 d-flex justify-content-center align-items-center px-4 py-3">
                  <button className="btn btn-secondary rounded-circle qr-control-button" onClick={(e) => { e.stopPropagation(); toggleCamera(); }} title={translations[lang]?.switch_camera}>
                    <i className="fas fa-sync-alt"></i>
                  </button>
                </div>
              </div>
            ) : (
              <div className="qr-placeholder-message text-light d-flex flex-column justify-content-center align-items-center flex-grow-1">
                <i className="fas fa-qrcode fa-5x mb-3"></i>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QrScannerModal;
